// Touch on a phone: pull the board down to refresh it, swipe sideways between days or stages, and hold a finger on a
// widget for its menu. Each leaves alone a touch that something else takes: words being written, a widget's own
// frame, a row that scrolls sideways, and the screen's edges, where the browser's own back swipe starts.

import { useEffect, useRef } from "react";

const EDGE = 24;

/** Where a touch began on something that takes touches itself. */
export function ownsTouch(t: EventTarget | null): boolean {
  const el = t instanceof Element ? t : null;
  // contenteditable of any kind but false: a doc's editor is "true", a mind map's idea "plaintext-only"
  return !!el?.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), iframe, [data-own-touch]');
}

/** Whether something between the touch and the area scrolls sideways itself (a row of tabs). */
function scrollsSideways(t: EventTarget | null, stop: Element): boolean {
  let el = t instanceof Element ? t : null;
  while (el && el !== stop) {
    if (el.scrollWidth > el.clientWidth + 2) {
      const o = getComputedStyle(el).overflowX;
      if (o === "auto" || o === "scroll") return true;
    }
    el = el.parentElement;
  }
  return false;
}

export const PULL_AT = 64;

/**
 * Pull down from the top of the scroller to refresh: the mark follows the finger, and past PULL_AT a release runs
 * refresh, the mark spinning until it is done (at least a moment, so it is seen). The mark is moved directly, so a
 * pull never draws the board again.
 */
export function usePullToRefresh(el: HTMLElement | null, mark: HTMLElement | null, refresh: () => Promise<unknown>, on = true) {
  const run = useRef(refresh);
  run.current = refresh;
  useEffect(() => {
    if (!el || !on) return;
    let x0 = 0;
    let y0 = 0;
    let tracking = false;
    let pulling = false;
    let busy = false;
    let dist = 0;
    const show = (d: number, state: "" | "is-ready" | "is-busy") => {
      const m = mark;
      if (!m) return;
      m.style.transform = `translate(-50%, ${Math.round(d - 52)}px) rotate(${Math.round(d * 4)}deg)`;
      m.style.opacity = d > 4 ? String(Math.min(1, d / PULL_AT)) : "0";
      m.className = `phone-pull ${state}`;
    };
    const down = (e: TouchEvent) => {
      tracking = !busy && e.touches.length === 1 && el.scrollTop <= 0 && !ownsTouch(e.target);
      if (!tracking) return;
      pulling = false;
      dist = 0;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    };
    const move = (e: TouchEvent) => {
      if (!tracking) return;
      const dx = e.touches[0].clientX - x0;
      const dy = e.touches[0].clientY - y0;
      if (!pulling) {
        // a sideways or upward start is a swipe or a scroll, not a pull
        if (dy < -4 || Math.abs(dx) > 12) tracking = false;
        if (!tracking || dy < 8 || Math.abs(dx) > dy) return;
        if (el.scrollTop > 0) {
          tracking = false;
          return;
        }
        pulling = true;
      }
      dist = Math.max(0, Math.min(120, (dy - 8) * 0.5));
      if (e.cancelable) e.preventDefault();
      show(dist, dist >= PULL_AT ? "is-ready" : "");
    };
    const up = () => {
      if (!tracking) return;
      tracking = false;
      if (!pulling) return;
      if (dist < PULL_AT) {
        show(0, "");
        return;
      }
      busy = true;
      show(PULL_AT, "is-busy");
      const t0 = Date.now();
      void run.current()
        .catch(() => {})
        .finally(() =>
          setTimeout(
            () => {
              busy = false;
              show(0, "");
            },
            Math.max(0, 650 - (Date.now() - t0)),
          ),
        );
    };
    el.addEventListener("touchstart", down, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", up);
    el.addEventListener("touchcancel", up);
    return () => {
      el.removeEventListener("touchstart", down);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", up);
      el.removeEventListener("touchcancel", up);
    };
  }, [el, mark, on]);
}

/** A quick sideways swipe across the area: 1 for the next (finger moving left), -1 for the one before. */
export function useSwipe(el: HTMLElement | null, onSwipe: (dir: 1 | -1) => void, on = true) {
  const go = useRef(onSwipe);
  go.current = onSwipe;
  useEffect(() => {
    if (!el || !on) return;
    let x0 = 0;
    let y0 = 0;
    let t0 = 0;
    let ok = false;
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      ok = e.touches.length === 1 && t.clientX > EDGE && t.clientX < window.innerWidth - EDGE && !ownsTouch(e.target) && !scrollsSideways(e.target, el);
      x0 = t.clientX;
      y0 = t.clientY;
      t0 = Date.now();
    };
    const up = (e: TouchEvent) => {
      if (!ok) return;
      ok = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      if (Date.now() - t0 < 700 && Math.abs(dx) > 64 && Math.abs(dy) < Math.abs(dx) * 0.5) go.current(dx < 0 ? 1 : -1);
    };
    const cancel = () => (ok = false);
    el.addEventListener("touchstart", down, { passive: true });
    el.addEventListener("touchend", up);
    el.addEventListener("touchcancel", cancel);
    return () => {
      el.removeEventListener("touchstart", down);
      el.removeEventListener("touchend", up);
      el.removeEventListener("touchcancel", cancel);
    };
  }, [el, on]);
}

/**
 * A finger held still on a widget for half a second opens its menu, as a right-click does on a computer. The tap
 * that ends the hold does nothing else. Widgets are found by data-widget under the area.
 */
export function useLongPress(el: HTMLElement | null, onHold: (id: string) => void, on = true) {
  const hold = useRef(onHold);
  hold.current = onHold;
  useEffect(() => {
    if (!el || !on) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let x0 = 0;
    let y0 = 0;
    const stop = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const swallow = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
    };
    const down = (e: TouchEvent) => {
      stop();
      if (e.touches.length !== 1 || ownsTouch(e.target)) return;
      const id = (e.target instanceof Element ? e.target.closest<HTMLElement>("[data-widget]") : null)?.dataset.widget;
      if (!id) return;
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
      timer = setTimeout(() => {
        timer = null;
        navigator.vibrate?.(8);
        // the click this touch ends in is the hold's, not a tap on what is under it
        window.addEventListener("click", swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 700);
        hold.current(id);
      }, 480);
    };
    const move = (e: TouchEvent) => {
      if (timer && Math.hypot(e.touches[0].clientX - x0, e.touches[0].clientY - y0) > 8) stop();
    };
    el.addEventListener("touchstart", down, { passive: true });
    el.addEventListener("touchmove", move, { passive: true });
    el.addEventListener("touchend", stop);
    el.addEventListener("touchcancel", stop);
    return () => {
      stop();
      el.removeEventListener("touchstart", down);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", stop);
      el.removeEventListener("touchcancel", stop);
    };
  }, [el, on]);
}
